import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
  type UseQueryResult,
} from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import type {
  AcceptInviteInput,
  ChangePasswordInput,
  ForgotPasswordInput,
  InviteLookup,
  LoginInput,
  ResetPasswordInput,
  SessionPayload,
  VerifyOtpInput,
} from '@nest-recruit/shared';
import { ApiError, apiDelete, apiGet, apiPost } from '../../../lib/api-client';
import { useSession } from '../../../stores/session';
import { useToasts } from '../../../stores/toasts';

/** One key for the session, so anything can invalidate it without a string literal. */
export const SESSION_QUERY_KEY = ['session'] as const;

export interface LoginResponse {
  readonly outcome: 'signed_in' | 'two_factor_required';
  readonly user: SessionPayload | null;
}

export interface TwoFactorSetup {
  readonly otpauthUri: string;
  readonly qrDataUrl: string;
  readonly secret: string;
}

export interface ActiveSession {
  readonly id: string;
  readonly ipAddress: string | null;
  readonly userAgent: string | null;
  readonly lastSeenAt: string;
  readonly expiresAt: string;
  readonly isCurrent: boolean;
  readonly isImpersonating: boolean;
}

/**
 * Fetches the session, and mirrors it into the client store.
 *
 * A plain function rather than only a hook, because the router's `beforeLoad` gate
 * needs the same answer before any component renders. Both paths go through one
 * cache entry, so the gate and the screen can never disagree about who is signed
 * in — and the mirror lives here so it happens whichever path ran.
 *
 * A 401 is an answer, not a failure: it resolves to `null` rather than throwing, so
 * "signed out" never looks like "the server is broken".
 */
export async function fetchSession(): Promise<SessionPayload | null> {
  try {
    const payload = await apiGet<SessionPayload>('/auth/me');
    useSession.getState().setSession(payload);
    return payload;
  } catch (error) {
    if (error instanceof ApiError && (error.isUnauthenticated || error.needsTwoFactor)) {
      useSession.getState().setSession(null);
      return null;
    }
    throw error;
  }
}

/**
 * The session query. Everything on screen depends on it, so it is deliberately
 * quiet: it never retries a 401, never refetches on focus, and never goes stale on
 * its own — the mutations that change who you are invalidate it explicitly.
 */
export function useSessionQuery(): UseQueryResult<SessionPayload | null, ApiError> {
  return useQuery<SessionPayload | null, ApiError>({
    queryKey: SESSION_QUERY_KEY,
    queryFn: fetchSession,
    staleTime: Number.POSITIVE_INFINITY,
    gcTime: Number.POSITIVE_INFINITY,
    retry: false,
    refetchOnWindowFocus: false,
    // The one query that must not keep the previous answer: showing the last
    // person's session while a new one loads is the worst possible stale read.
    placeholderData: undefined,
  });
}

export function useLogin() {
  const queryClient = useQueryClient();
  return useMutation<LoginResponse, ApiError, LoginInput>({
    mutationFn: (input) => apiPost<LoginResponse>('/auth/login', input),
    onSuccess: async (result) => {
      if (result.outcome === 'signed_in' && result.user) {
        // Seeded rather than refetched: the login response already carries the
        // whole payload, and a second round trip would show an empty shell for the
        // length of it.
        queryClient.setQueryData(SESSION_QUERY_KEY, result.user);
        useSession.getState().setSession(result.user);
      }
    },
  });
}

interface LogoutOptions {
  /**
   * False on a screen outside the shell that carries on once signed out — AUTH-04's
   * signed-in visitor, who signs out to accept the invite on the same page.
   */
  readonly isLandingOnSignIn?: boolean;
}

/**
 * Sign out, and land on the sign-in screen.
 *
 * The navigation lives here rather than with the caller, because the order is the
 * whole point (D8): the shell reads the session with `useRequiredSession`, so the
 * store may let go of it only once the shell is gone. Clearing first and navigating
 * after drew the shell once with no session — the "Something went wrong" screen.
 */
export function useLogout({ isLandingOnSignIn = true }: LogoutOptions = {}) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  return useMutation<void, ApiError, void>({
    mutationFn: () => apiPost<void>('/auth/logout'),
    // Whether or not the request succeeded: a sign-out that leaves the last
    // person's data on screen because the network hiccuped is a real leak.
    onSettled: async () => {
      // Before navigating: the sign-in route's gate reads this entry, and the old
      // session still in it would bounce the visitor straight back.
      queryClient.setQueryData(SESSION_QUERY_KEY, null);
      if (isLandingOnSignIn) await navigate({ to: '/login' });
      useSession.getState().setSession(null);
      // The next person to sign in on this browser must not see this one's data.
      queryClient.removeQueries({
        predicate: (query) => query.queryKey[0] !== SESSION_QUERY_KEY[0],
      });
    },
  });
}

export function useForgotPassword() {
  return useMutation<void, ApiError, ForgotPasswordInput>({
    mutationFn: (input) => apiPost<void>('/auth/forgot-password', input),
  });
}

export function useResetPassword() {
  return useMutation<void, ApiError, ResetPasswordInput>({
    mutationFn: (input) => apiPost<void>('/auth/reset-password', input),
  });
}

/**
 * F1.05, before the form: the invited person's name to prefill, or why the link is dead — so a
 * dead link says so straight away rather than after a password has been typed twice.
 */
export function useInviteLookup(token: string): UseQueryResult<InviteLookup, ApiError> {
  return useQuery<InviteLookup, ApiError>({
    queryKey: ['invite', token],
    queryFn: () => apiGet<InviteLookup>('/auth/invite', { query: { token } }),
    staleTime: Number.POSITIVE_INFINITY,
    retry: false,
    refetchOnWindowFocus: false,
  });
}

export function useAcceptInvite() {
  const queryClient = useQueryClient();
  return useMutation<SessionPayload | null, ApiError, AcceptInviteInput>({
    mutationFn: (input) => apiPost<SessionPayload | null>('/auth/accept-invite', input),
    onSuccess: (payload) => {
      if (!payload) return;
      queryClient.setQueryData(SESSION_QUERY_KEY, payload);
      useSession.getState().setSession(payload);
    },
  });
}

export function useChangePassword() {
  return useMutation<void, ApiError, ChangePasswordInput>({
    mutationFn: (input) => apiPost<void>('/auth/change-password', input),
  });
}

export function useBeginTwoFactor() {
  return useMutation<TwoFactorSetup, ApiError, void>({
    mutationFn: () => apiPost<TwoFactorSetup>('/auth/2fa/setup'),
  });
}

export function useEnableTwoFactor() {
  const queryClient = useQueryClient();
  return useMutation<{ recoveryCodes: readonly string[] }, ApiError, { code: string }>({
    mutationFn: (input) => apiPost<{ recoveryCodes: readonly string[] }>('/auth/2fa/enable', input),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: SESSION_QUERY_KEY });
    },
  });
}

export function useVerifyOtp() {
  const queryClient = useQueryClient();
  return useMutation<void, ApiError, VerifyOtpInput>({
    mutationFn: (input) => apiPost<void>('/auth/2fa/verify', input),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: SESSION_QUERY_KEY });
    },
  });
}

export function useActiveSessions() {
  return useQuery<readonly ActiveSession[], ApiError>({
    queryKey: ['auth', 'sessions'],
    queryFn: () => apiGet<readonly ActiveSession[]>('/auth/sessions'),
  });
}

export function useRevokeSession() {
  const queryClient = useQueryClient();
  return useMutation<void, ApiError, string>({
    mutationFn: (id) => apiDelete<void>(`/auth/sessions/${id}`),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['auth', 'sessions'] });
    },
  });
}

/**
 * Starting or stopping View as changes who the server says you are, so it is a
 * session boundary (CLAUDE.md §5a rule 7).
 *
 * Everything cached belongs to the previous identity, so all of it goes — the
 * session included. Going home then re-runs the app gate, which reads the new
 * session (and mirrors it into the store the shell draws from), the settings and the
 * person's preferences afresh. The router re-runs the gate even when the viewer is
 * already home.
 *
 * D21: this used to clear the cache and then invalidate it, which finds nothing
 * left to invalidate. The screen kept showing the HR Lead until a reload, while
 * every request went out as the person being viewed.
 */
async function switchIdentity(
  queryClient: QueryClient,
  goHome: () => Promise<void>,
): Promise<void> {
  queryClient.clear();
  await goHome();
}

export function useStartImpersonation() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const showToast = useToasts((state) => state.show);
  return useMutation<
    { endsAt: string; subjectName: string },
    ApiError,
    { userId: string; reason: string }
  >({
    mutationFn: (input) =>
      apiPost<{ endsAt: string; subjectName: string }>('/auth/impersonate', input),
    // The toast is raised here, not by the dialog: going home unmounts the dialog,
    // and a caller's own callbacks never run once it is gone.
    onSuccess: async (result) => {
      showToast({
        tone: 'warning',
        message: `Now viewing as ${result.subjectName}`,
        detail: 'Read-only. Use the banner at the top to stop.',
      });
      await switchIdentity(queryClient, () => navigate({ to: '/' }));
    },
  });
}

export function useStopImpersonation() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  return useMutation<void, ApiError, void>({
    mutationFn: () => apiPost<void>('/auth/impersonate/stop'),
    // Settled, not only succeeded: a refusal usually means the thirty minutes ran
    // out already, and re-reading the session is what puts the screen right then too.
    onSettled: () => switchIdentity(queryClient, () => navigate({ to: '/' })),
  });
}

interface WelcomeSeenContext {
  readonly previous: SessionPayload | null | undefined;
}

export function useMarkWelcomeSeen() {
  const queryClient = useQueryClient();
  return useMutation<void, ApiError, void, WelcomeSeenContext>({
    mutationFn: () => apiPost<void>('/auth/welcome-seen'),
    // Deliberately synchronous — no `await cancelQueries` first. The button navigates
    // in the same click, and the router's gate reads this cache entry to decide
    // whether to send the user back to AUTH-09. Written any later, the gate still
    // sees "not seen" and the Start button looks like it did nothing.
    onMutate: (): WelcomeSeenContext => {
      const previous = queryClient.getQueryData<SessionPayload | null>(SESSION_QUERY_KEY);
      if (previous) {
        const seen = { ...previous, hasSeenWelcome: true };
        queryClient.setQueryData(SESSION_QUERY_KEY, seen);
        useSession.getState().setSession(seen);
      }
      return { previous };
    },
    onError: (_error, _input, context) => {
      if (!context?.previous) return;
      queryClient.setQueryData(SESSION_QUERY_KEY, context.previous);
      useSession.getState().setSession(context.previous);
    },
  });
}
